import Vue from 'vue';
import migrate from '@/migrations/instances';
import PageSettings from '@/modules/PageSettings.vue';
import getPropDefaults from '@/helpers/getPropDefaults';
import uniqueId from '@/helpers/uniqueId';
import api from '@/store/helpers/api';
import removeDisabledInstances from '@/store/helpers/removeDisabledInstances';

export default {
  state: {
    pageList: [],
    parentPageList: [],
    pages: {},
    deletedPages: [],
  },
  mutations: {
    RESET_PAGES(state) {
      state.pageList = [];
      state.parentPageList = [];

      const activePage = state.pages[state.activePageId];
      const pages = {};

      if (activePage) {
        pages[state.activePageId] = activePage; // avoid erasing active page
      }
      state.pages = pages;
    },
    REGISTER_PAGE(state, page) {
      state.pageList.push(page.id);

      if (Array.isArray(page.translations)) {
        state.parentPageList.push(page.id);
      }
    },
    SET_PAGE(state, page) {
      let pageToSet = page;
      const existingPage = state.pages[page.id];
      if (existingPage) {
        pageToSet = { ...existingPage, ...page };
      }
      Vue.set(state.pages, page.id, pageToSet);
    },
    SET_PAGE_STATUS(state, payload) {
      const { pageId, isPublished } = payload;

      if (state.pages[pageId]) {
        Vue.set(state.pages[pageId], 'isPublished', isPublished);
      }
    },
    SET_PAGE_DIRTINESS(state, payload) {
      const { pageId, isDirty } = payload;

      if (state.pages[pageId]) {
        Vue.set(state.pages[pageId], 'isDirty', isDirty);
      }
    },
    SET_DELETED_PAGES(state, deletedPages) {
      state.deletedPages = deletedPages;
    },
    REMOVE_DELETED_PAGE(state, deletedPageId) {
      const index = state.deletedPages.findIndex(
        (page) => page.id === deletedPageId,
      );
      state.deletedPages.splice(index, 1);
    },
  },
  getters: {
    pageExists: (state) => (pageId) => state.pageList.includes(pageId),
    // eslint-disable-next-line arrow-body-style
    urlPathExists: (state) => (urlPath, language) => {
      // eslint-disable-next-line arrow-body-style
      return (
        typeof state.pageList
          .map((pageId) => state.pages[pageId])
          .find((page) => {
            return page.language === language && page.urlPath === urlPath;
          }) !== 'undefined'
      );
    },
    pagesArray: (state) => (lang) => {
      const pag = Object.values(state.pages);

      return lang ? pag.filter(({ language }) => language === lang) : pag;
    },
  },
  actions: {
    async storePage({ commit, state, getters, dispatch }, doc) {
      const page = doc;

      const { id, instances, translations, settingsInstance } = page;

      delete page.instances;

      if (Array.isArray(translations)) {
        page.translationIds = translations.map((translation) => translation.id);
        page.translations = translations.map(
          (translation) => translation.language,
        );
      }

      if (!page.language) {
        page.language = 'pt';
      }

      commit('SET_PAGE', page);

      if (!getters.pageExists(id)) {
        commit('REGISTER_PAGE', page);
      }

      if (typeof instances !== 'undefined') {
        const pageSettings = instances[settingsInstance];
        const migratedSettings = migrate(pageSettings, state.customerSettings);
        dispatch('storeInstance', {
          id: settingsInstance,
          ...migratedSettings,
        });

        // Use Promise.all to await for each async generator function which waits for loadGlobal
        await Promise.all(
          page.instanceList.map(async (instanceId) => {
            const instance = migrate(instances[instanceId], {
              ...state.customerSettings,
              ...pageSettings.data,
            });

            dispatch('storeInstance', { ...instance, id: instanceId });
          }),
        );
      }

      if (Array.isArray(translations)) {
        await Promise.all(
          translations.map(async (translation) => {
            await dispatch('storePage', translation);
          }),
        );
      }
    },
    loadDeletedPages({ commit }) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve, reject) => {
        api
          .get('pages/trash')
          .then(async (response) => {
            const parents = response.data
              .filter((doc) => typeof doc.parentId === 'undefined')
              .map((doc) => ({
                ...doc,
                translations: [],
              }));

            response.data
              .filter((doc) => typeof doc.parentId === 'string')
              .forEach(({ parentId, ...doc }) => {
                const parentIndex = parents.findIndex(
                  (parent) => parent.id === parentId,
                );

                if (parentIndex > -1) {
                  parents[parentIndex].translations.push(doc);
                }
              });

            commit('SET_DELETED_PAGES', parents);
          })
          .then(resolve)
          .catch(reject)
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    loadPages({ commit, dispatch }) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve, reject) => {
        api
          .get('pages')
          .then(async (response) => {
            commit('RESET_PAGES');
            const parents = response.data
              .filter((doc) => typeof doc.parentId === 'undefined')
              .map((doc) => ({
                ...doc,
                translations: [],
              }));

            response.data
              .filter((doc) => typeof doc.parentId === 'string')
              .forEach(({ parentId, ...doc }) => {
                const parentIndex = parents.findIndex(
                  (parent) => parent.id === parentId,
                );

                if (parentIndex > -1) {
                  parents[parentIndex].translations.push(doc);
                }
              });

            await Promise.all(
              parents.map(async (doc) => {
                await dispatch('storePage', doc);
              }),
            );
          })
          .then(resolve)
          .catch(reject)
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    loadPage({ commit }, { language, urlPath }) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve, reject) => {
        api
          .get(`pages/${language}/${urlPath}`)
          .then((response) => {
            resolve(response.data);
          })
          .catch(reject)
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    addPage({ commit }, basePage) {
      return new Promise((resolve) => {
        const page = basePage;
        const settingsInstanceId = uniqueId();
        const settingsInstance = {
          name: 'Configurações',
          module: 'PageSettings',
          data: {
            ...getPropDefaults(PageSettings),
          },
        };

        if (page.settingsInstance) {
          // new page is template, apply template's page settings
          settingsInstance.data = {
            ...settingsInstance.data,
            ...page.instances[page.settingsInstance].data,
          };
          delete page.instances[page.settingsInstance]; // delete template settings data
        }

        commit('SET_LOADING_STATE', true);

        page.isPublished = false;
        page.isDirty = true;
        page.settingsInstance = settingsInstanceId;
        page.language = 'pt';

        page.instances[page.settingsInstance] = settingsInstance;

        api
          .post('pages/pt', page)
          .then(resolve)
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    movePageToTrash({ commit, state, getters, dispatch }, page) {
      return new Promise((resolve) => {
        const { id: pageId, language, urlPath, translationIds } = page;

        if (getters.pageExists(pageId)) {
          commit('SET_LOADING_STATE', true);

          const requests = [];

          requests.push(
            language === 'pt'
              ? api.delete(`pages/${language}/${urlPath}`)
              : api.delete(`pages/trash/${pageId}`),
          );

          if (Array.isArray(translationIds)) {
            translationIds
              .filter(getters.pageExists)
              .map((translationId) => state.pages[translationId])
              .forEach((translation) => {
                requests.push(
                  api.delete(
                    `pages/${translation.language}/${translation.urlPath}`,
                  ),
                );
              });
          }

          Promise.all(requests)
            .then(() => dispatch('loadPages'))
            .then(resolve)
            .catch((error) => console.log(error))
            .finally(() => commit('SET_LOADING_STATE', false));
        }
      });
    },
    restorePage({ commit, dispatch }, page) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve) => {
        const { id: pageId, language, translations } = page;
        const requests = [api.patch(`pages/trash/${pageId}`)];

        if (Array.isArray(translations) && language === 'pt') {
          translations.forEach(({ id }) => {
            requests.push(api.patch(`pages/trash/${id}`));
          });
        }

        Promise.all(requests)
          .then(() => dispatch('loadPages'))
          .then(resolve)
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    discardChanges({ commit, dispatch }, page) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve, reject) => {
        const { urlPath, language } = page;

        api
          .patch(`pages/discard/${language}/${urlPath}`)
          .then(() => {
            dispatch('loadPage', page).then((pageBase) => {
              dispatch('storePage', pageBase);
              dispatch('validateActiveContainer');
              resolve();
            });
          })
          .catch(reject)
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    deletePage({ commit, dispatch }, page) {
      commit('SET_LOADING_STATE', true);

      return new Promise((resolve) => {
        const { id: pageId, language, translations } = page;
        const requests = [api.delete(`pages/trash/${pageId}`)];

        if (Array.isArray(translations) && language === 'pt') {
          translations.forEach(({ id }) => {
            requests.push(api.delete(`pages/trash/${id}`));
          });
        }

        Promise.all(requests)
          .then(() => dispatch('loadDeletedPages'))
          .then(resolve)
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    editPageBasicInfo({ commit, getters, dispatch }, payload) {
      return new Promise((resolve) => {
        const {
          page: { id: pageId, language, urlPath },
          newBasicInfo,
        } = payload;

        if (getters.pageExists(pageId)) {
          commit('SET_LOADING_STATE', true);

          api
            .patch(`pages/${language}/${urlPath}`, newBasicInfo)
            .then(() => dispatch('loadPages'))
            .then(resolve)
            .catch((error) => console.log(error))
            .finally(() => commit('SET_LOADING_STATE', false));
        }
      });
    },
    createPageTranslation({ commit, state, getters, dispatch }, payload) {
      return new Promise((resolve) => {
        const {
          page: { id: pageId, urlPath },
          language,
        } = payload;

        if (getters.pageExists(pageId)) {
          dispatch('loadPage', { urlPath, language: 'pt' }).then((basePage) => {
            const page = basePage;

            commit('SET_LOADING_STATE', true);

            let cloneTitle = `${page.title}`;
            let cloneUrlPath = page.urlPath;
            let urlPathExistsCount = 0;

            while (getters.urlPathExists(cloneUrlPath, language)) {
              urlPathExistsCount += 1;

              cloneUrlPath = `${page.urlPath}-${urlPathExistsCount + 1}`;
              cloneTitle = `${page.title} (${urlPathExistsCount + 1})`;
            }

            page.parentId = basePage.id;
            page.title = cloneTitle;
            page.urlPath = cloneUrlPath;
            page.isPublished = false;
            page.isDirty = true;

            const { instances, instanceList } = removeDisabledInstances(
              page,
              state,
              getters,
            );

            page.instances = instances;
            page.instanceList = instanceList;

            api
              .post(`pages/${language}`, page)
              .then(() => dispatch('loadPages'))
              .then(() => resolve({ ...page, language }))
              .catch((error) => console.log(error))
              .finally(() => commit('SET_LOADING_STATE', false));
          });
        }
      });
    },
    clonePage({ commit, state, getters, dispatch }, payload) {
      return new Promise((resolve) => {
        const { id: pageId } = payload;

        if (getters.pageExists(pageId)) {
          dispatch('loadPage', payload).then((basePage) => {
            const page = basePage;

            commit('SET_LOADING_STATE', true);

            let cloneTitle = `Cópia de ${page.title}`;
            let cloneUrlPath = `copia-de-${page.urlPath}`;
            let urlPathExistsCount = 0;

            while (getters.urlPathExists(cloneUrlPath, 'pt')) {
              urlPathExistsCount += 1;

              cloneUrlPath = `copia-de-${page.urlPath}-${
                urlPathExistsCount + 1
              }`;
              cloneTitle = `Cópia de ${page.title} (${urlPathExistsCount + 1})`;
            }

            page.title = cloneTitle;
            page.urlPath = cloneUrlPath;
            page.isPublished = false;
            page.isDirty = true;

            const { instances, instanceList } = removeDisabledInstances(
              page,
              state,
              getters,
            );

            page.instances = instances;
            page.instanceList = instanceList;

            api
              .post('pages/pt', page)
              .then(() => dispatch('loadPages'))
              .then(() => resolve(page))
              .catch((error) => console.log(error))
              .finally(() => commit('SET_LOADING_STATE', false));
          });
        }
      });
    },
    exportPage({ commit }, page) {
      return new Promise((resolve) => {
        commit('SET_LOADING_STATE', true);

        api
          .get(`pages/export/${page.id}`)
          .then(resolve)
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
    setPageStatus({ commit }, payload) {
      return new Promise((resolve) => {
        const {
          page: { id: pageId, language, urlPath },
          isPublished,
        } = payload;

        commit('SET_LOADING_STATE', true);

        api
          .put(`pages/${language}/${urlPath}/isPublished`, {
            isPublished,
          })
          .then(() => {
            commit('UPDATE_SAVE_TIME');
            commit('SET_PAGE_STATUS', {
              pageId,
              isPublished,
            });

            commit('SET_PAGE_DIRTINESS', {
              pageId,
              isDirty: !isPublished,
            });

            resolve();
          })
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      });
    },
  },
};
